import { computed, nextTick, onUnmounted, Ref, ref, watch } from 'vue';
import {
  AutoscaleInfo,
  Coordinate,
  createChart as createLightweightChart,
  IChartApi,
  ISeriesApi,
  LineData,
  LineStyle,
  LineWidth,
  MouseEventParams,
  Time,
} from 'lightweight-charts';
import { useColorMode, useElementSize } from '@vueuse/core';
import { SymbolCandleStickLineChartDataType } from '@core/symbol';

import {
  LINE_CHART_OPTIONS,
  SYMBOL_LINE_CHART_FILTERS,
  LINE_CHART_OPTIONS_LAYOUT_BACKGROUND_COLOR_PALETTE,
  CHART_PRICE_LINE_COLOR_PALETTE,
} from '@/modules/symbol';

const TOOLTIP_HORIZONTAL_DEFAULT_SHIFTED = 2;
const OVERLAY_HORIZONTAL_DEFAULT_SHIFTED = 4;

export default function useSymbolLineChart({
  chartContainerRef,
  tooltipRef,
  referencePrice,
  selectedTimespan,
  chartData,
  seriesColor,

  handlePointClick,
}: {
  chartContainerRef: Ref<HTMLElement | undefined>;
  tooltipRef: Ref<HTMLElement | undefined>;
  referencePrice: Ref<number>;
  selectedTimespan: Ref<string>;
  seriesColor: Ref<string>;
  chartData: Ref<SymbolCandleStickLineChartDataType>;

  handlePointClick: (value: LineData) => void;
}) {
  let chart: IChartApi | null = null;
  let series: ISeriesApi<'Line'> | null | undefined = null;

  const colorMode = useColorMode();

  const timePoint = ref<Coordinate | null>();
  const showTooltip = ref<boolean>(false);
  const shiftedCoordinateTooltip = ref<number>();
  const tooltipDate = ref<Time>();
  const showOverlay = ref(false);
  const overlayWidth = ref<number>();
  const shiftedCoordinateOverlay = ref<number>();

  const { width } = useElementSize(chartContainerRef);

  const lastDataItem = computed(() => {
    return chartData.value[chartData.value.length - 1];
  });

  const chartContainerWidth = computed(
    () => chartContainerRef.value?.clientWidth || 0
  );
  const chartContainerHeight = computed(
    () => chartContainerRef.value?.clientHeight || 0
  );

  const referencePriceLineData = computed(() => ({
    price: referencePrice.value,
    color: CHART_PRICE_LINE_COLOR_PALETTE[colorMode.value],
    lineWidth: 2 as LineWidth,
    lineStyle: LineStyle.Dashed,
    axisLabelVisible: false,
    title: 'reference price',
  }));

  const shouldCreateChart = computed(
    () => chartContainerRef.value && chartData.value.length
  );

  const addReferencePriceLineSeries = () => {
    series?.createPriceLine(referencePriceLineData.value);
  };

  const setPriceScale = () => {
    if (chart) {
      series = chart.addLineSeries({
        ...LINE_CHART_OPTIONS,
        autoscaleInfoProvider: (original: () => AutoscaleInfo | null) => {
          const scaleInfo = original();

          if (scaleInfo !== null) {
            if (scaleInfo.priceRange.maxValue < referencePrice.value) {
              scaleInfo.priceRange.maxValue = referencePrice.value;
            }

            if (scaleInfo.priceRange.minValue > referencePrice.value) {
              scaleInfo.priceRange.minValue = referencePrice.value;
            }
          }

          return scaleInfo;
        },
      });
    }
  };

  const createSeriesAndSetData = () => {
    series = chart?.addLineSeries(LINE_CHART_OPTIONS);

    if (selectedTimespan.value === SYMBOL_LINE_CHART_FILTERS.oneDay.id) {
      setPriceScale();
      addReferencePriceLineSeries();
    }

    series?.setData(chartData.value);
    chart?.timeScale().fitContent();

    setSeriesColor();
  };

  const isChartUnClicked = (param: MouseEventParams) => {
    return (
      !param.point ||
      !param.time ||
      param.point.x < 0 ||
      param.point.x > chartContainerWidth.value ||
      param.point.y < 0 ||
      param.point.y > chartContainerHeight.value
    );
  };

  const setSeriesColor = () => {
    series?.applyOptions({
      color: seriesColor.value,
    });
  };

  const handleCrosshairTooltip = async (
    param: MouseEventParams,
    seriesData: LineData
  ) => {
    if (isChartUnClicked(param)) {
      showTooltip.value = false;
    } else {
      tooltipDate.value = param.time;
      timePoint.value = chart?.timeScale().timeToCoordinate(seriesData.time);

      if (!timePoint.value) {
        return;
      }

      if (tooltipRef.value && timePoint.value) {
        shiftedCoordinateTooltip.value =
          timePoint.value - tooltipRef.value.clientWidth / 2;

        shiftedCoordinateTooltip.value = Math.max(
          TOOLTIP_HORIZONTAL_DEFAULT_SHIFTED,
          Math.min(
            chartContainerWidth.value -
              tooltipRef.value.clientWidth -
              TOOLTIP_HORIZONTAL_DEFAULT_SHIFTED,
            shiftedCoordinateTooltip.value
          )
        );

        showTooltip.value = true;
      }
    }
  };

  const handleCrosshairOverlay = (
    param: MouseEventParams,
    seriesData: LineData
  ) => {
    if (isChartUnClicked(param)) {
      showOverlay.value = false;
    } else {
      timePoint.value = chart?.timeScale().timeToCoordinate(seriesData.time);

      if (!timePoint.value) {
        return;
      }

      if (timePoint.value) {
        overlayWidth.value =
          chartContainerWidth.value -
          timePoint.value -
          OVERLAY_HORIZONTAL_DEFAULT_SHIFTED;

        shiftedCoordinateOverlay.value =
          timePoint.value + OVERLAY_HORIZONTAL_DEFAULT_SHIFTED;

        showOverlay.value = true;
      }
    }
  };

  const checkCrosshairMove = () => {
    if (chart) {
      chart.subscribeCrosshairMove((param) => {
        if (series) {
          const seriesData = param.seriesData.get(series) as LineData;

          handlePointClick(seriesData);
          handleCrosshairTooltip(param, seriesData);
          handleCrosshairOverlay(param, seriesData);
        }
      });
    }
  };

  const createChart = () => {
    if (chartContainerRef.value) {
      chart = createLightweightChart(chartContainerRef.value, {
        ...LINE_CHART_OPTIONS,
        layout: {
          attributionLogo: false,
          background: {
            color:
              LINE_CHART_OPTIONS_LAYOUT_BACKGROUND_COLOR_PALETTE[
                colorMode.value
              ],
          },
        },
      });

      createSeriesAndSetData();
      checkCrosshairMove();
    }
  };

  const updateChart = () => {
    series?.update(lastDataItem.value);
    chart?.timeScale().fitContent();
  };

  const deleteChart = () => {
    chart?.remove();

    chart = null;
  };

  const recreateChart = () => {
    deleteChart();
    createChart();
  };

  onUnmounted(() => {
    deleteChart();
  });

  watch(
    () => selectedTimespan.value,
    () => {
      if (series && chart) {
        chart.removeSeries(series);
      }

      createSeriesAndSetData();
    }
  );

  watch(
    () => seriesColor.value,
    () => {
      setSeriesColor();
    },
    { immediate: true }
  );

  watch(
    () => lastDataItem.value,
    () => {
      updateChart();
    },
    { deep: true }
  );

  watch(
    () => [chartContainerRef.value],
    async () => {
      if (shouldCreateChart.value) {
        await nextTick();

        createChart();
      } else {
        deleteChart();
      }
    },
    { immediate: true }
  );

  watch([colorMode, width], () => {
    if (shouldCreateChart.value) {
      recreateChart();
    }
  });

  return {
    shiftedCoordinateTooltip,
    showTooltip,
    tooltipDate,
    overlayWidth,
    shiftedCoordinateOverlay,
    showOverlay,
  };
}
